import * as React from 'react';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { PublicPageWrapper } from '../_pageWrappers/PublicPageWrapper';
import {
    createSurvyResponse,
    getSurvy,
    getSurvyResponse
} from '../survy/survyClientApi';
import { Survy, SurvyResponse } from '../../amorg-common';
import { applicationEndpoints } from '../../amorg-common/applicationEndpoints';

type PageState = {
    loading: boolean;
    survey: Survy | null;
    response: SurvyResponse | null;
    error: string;
};

export function SurvyStartPage(): JSX.Element {
    const params = useParams<{ key: string; surveyId: string; responseId: string }>();
    const navigate = useNavigate();
    const [state, setState] = useState<PageState>({
        loading: true,
        survey: null,
        response: null,
        error: ''
    });

    useEffect(() => {
        const load = async () => {
            try {
                console.log('key:', params.key, 'surveyId:', params.surveyId, 'responseId:', params.responseId);
                const survey = await getSurvy(params.key || '', params.surveyId || '');
                const response = await getSurvyResponse(params.key || '', params.responseId || '');

                if (response && response.surveyId !== params.surveyId) {
                    setState({
                        loading: false,
                        survey,
                        response: null,
                        error: 'Besvarelses-id er allerede brugt til en anden undersøgelse.'
                    });
                    return;
                }

                if (survey && response) {
                    navigateToNextStep(survey, response);
                    return;
                }

                setState({ loading: false, survey, response, error: survey ? '' : 'Undersøgelsen blev ikke fundet.' });
            }
            catch {
                setState({ loading: false, survey: null, response: null, error: 'Kunne ikke indlæse undersøgelsen.' });
            }
        };

        load();
    }, [navigate, params.key, params.surveyId, params.responseId]);

    function navigateToNextStep(survey: Survy, response: SurvyResponse) {

        if (response.finalized) {
            navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_thanks, {
                key: params.key,
                surveyId: params.surveyId,
                responseId: params.responseId
            }));
            return;
        }

        const firstUnanswered = findFirstUnansweredQuestionIndex(survey, response);
        if (firstUnanswered === null)
            navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_thanks, {
                key: params.key,
                surveyId: params.surveyId,
                responseId: params.responseId
            }));
        else
            navigate(generatePath(applicationEndpoints.survy_key_surveyId_responseId_questions_questionIndex, {
                key: params.key,
                surveyId: params.surveyId,
                responseId: params.responseId,
                questionIndex: `${firstUnanswered}`
            }));
    }

    async function handleStart() {
        if (!state.survey)
            return;

        try {
            await createSurvyResponse(params.key || '', params.responseId || '', params.surveyId || '');
            const response = await getSurvyResponse(params.key || '', params.responseId || '');
            if (!response)
                throw new Error('response missing after create');

            if (response.surveyId !== params.surveyId) {
                setState({ ...state, error: 'Besvarelses-id er allerede brugt til en anden undersøgelse.' });
                return;
            }

            navigateToNextStep(state.survey, response);
        }
        catch {
            setState({ ...state, error: 'Kunne ikke starte besvarelsen.' });
        }
    }

    return (
        <PublicPageWrapper>
            <Container>
                {state.loading && <InfoText>Indlæser...</InfoText>}

                {!state.loading && state.error && <ErrorText>{state.error}</ErrorText>}

                {!state.loading && state.survey && (
                    <Panel>
                        <Title>Start undersøgelse</Title>
                        <Intro>{state.survey.introduction}</Intro>
                        <ButtonRow>
                            <PrimaryButton onClick={handleStart}>Start undersøgelsen</PrimaryButton>
                            {state.response && (
                                <GhostButton onClick={() => navigateToNextStep(state.survey as Survy, state.response as SurvyResponse)}>
                                    Fortsæt besvarelse
                                </GhostButton>
                            )}
                        </ButtonRow>
                    </Panel>
                )}
            </Container>
        </PublicPageWrapper>
    );
}

function findFirstUnansweredQuestionIndex(survey: Survy, response: SurvyResponse): number | null {
    for (let i = 0; i < survey.questions.length; i++) {
        if (!response.answers.find(a => a.questionIndex === i))
            return i;
    }

    return null;
}

const Container = styled.main`
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    padding: 32px 20px;
`;

const Panel = styled.section`
    background: #ffffff;
    border: 1px solid #d7d7d7;
    border-radius: 12px;
    padding: 24px;
`;

const Title = styled.h1`
    margin: 0 0 16px;
    font-size: 2rem;
    font-weight: 700;
`;

const Intro = styled.p`
    line-height: 1.6;
    white-space: pre-wrap;
`;

const InfoText = styled.p`
    margin: 0;
`;

const ErrorText = styled.p`
    margin: 0;
    color: #b22727;
`;

const ButtonRow = styled.div`
    margin-top: 24px;
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
    border: 0;
    border-radius: 8px;
    background: #11365b;
    color: #fff;
    padding: 12px 18px;
    cursor: pointer;
`;

const GhostButton = styled.button`
    border: 1px solid #11365b;
    border-radius: 8px;
    background: #fff;
    color: #11365b;
    padding: 12px 18px;
    cursor: pointer;
`;
